import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/**
 * What people wrote from the marketing site.
 *
 * Until SMTP is configured this screen is the inbox, not a copy of one, so it opens on what has
 * not been answered and shows the whole message. Answering happens in a mail client — the button
 * here only records that somebody did, so the pile means something.
 */
export default function Messages({ onExpired }) {
  const { t, i18n } = useTranslation()
  const [state, setState] = useState({ messages: [], waiting: 0 })
  const [all, setAll] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)

  const load = useCallback(() => {
    api
      .messages(all)
      .then((result) => {
        setState(result)
        setError(null)
      })
      .catch((failure) => {
        onExpired(failure)
        setError(failure.message)
      })
  }, [all, onExpired])

  useEffect(load, [load])

  async function mark(message, answered) {
    setBusy(message.id)
    try {
      await api.markAnswered(message.id, answered)
      load()
    } catch (failure) {
      onExpired(failure)
      setError(failure.message)
    } finally {
      setBusy(null)
    }
  }

  const when = (value) =>
    new Date(value).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <section className="card">
      <div className="card__head">
        <h2>{t('messages.title')}</h2>
        <label className="toggle">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          {t('messages.showAll')}
        </label>
      </div>

      {error && <p className="status status--bad">{error}</p>}

      <p className="muted small">{t('messages.waiting', { count: state.waiting })}</p>

      {state.messages.length === 0 ? (
        <p className="muted">{t('messages.none')}</p>
      ) : (
        <ul className="msgs">
          {state.messages.map((message) => (
            <li key={message.id} className={`msg${message.answeredAt ? ' msg--done' : ''}`}>
              <div className="msg__head">
                <strong>{message.name}</strong>
                <a href={`mailto:${message.email}`}>{message.email}</a>
                <span className="pill">{message.language.toUpperCase()}</span>
                {message.website && (
                  <a
                    href={message.website.startsWith('http') ? message.website : `https://${message.website}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {message.website}
                  </a>
                )}
                <span className="msg__when">{when(message.createdAt)}</span>
              </div>

              <p className="msg__body">{message.message}</p>

              <div className="msg__foot">
                {/* Null delivered_at is not a failure while there is no SMTP — say which it is. */}
                {!message.deliveredAt && <span className="muted small">{t('messages.notMailed')}</span>}
                <button
                  type="button"
                  className="button button--quiet"
                  disabled={busy === message.id}
                  onClick={() => mark(message, !message.answeredAt)}
                >
                  {message.answeredAt ? t('messages.reopen') : t('messages.markAnswered')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
